import {
    EventPanelFormType,
    EventPanelSelfInitiate,
    EventPanelView,
    PhoneCallDto,
} from '@mark43/cad-api';
import { MutableRefObject, useCallback, useRef, useState } from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';
import { useStableEffect } from '../../common/hooks/use-stable-effect';
import { LocationStoreView } from '../../location/types';
import { phoneCallDtoToPhoneCallOption } from '../../phone-calls/phone-call-dto-to-phone-call-option';
import { eventPanelFormInitialValues } from '../event-panel-form/event-panel-form-stub-values';
import { useEventPanelFormValidationResolver } from '../event-panel-form/event-panel-form-validation-resolver';
import { LocallyRemovedEventPanelFormFieldsStore } from '../event-panel-form/form-persistence/locally-removed-fields-store';
import { preserveLocallyChangedFields } from '../event-panel-form/form-persistence/merge-preserved-fields';
import { useLocallyRemovedEventFormFieldsStore } from '../event-panel-form/form-persistence/use-locally-removed-fields';
import { useIntegratedEventPanelViewQuery } from './use-integrated-event-panel-view-query';
import { EventPanelMetadataStore } from './event-panel-metadata-store';
import {
    useFormHasHadDataSetAtLeastOnceContext,
    useSetFormHasHadDataSetAtLeastOnceContext,
} from './form-has-had-data-set-at-least-once';
import { useEventPanelViewQuery } from './use-event-panel-view-query';
import { UseQueryOptions } from '@tanstack/react-query';

type EventDataRefreshOptions = {
    eventPanelView: EventPanelView;
    phoneCall?: PhoneCallDto;
    location?: LocationStoreView;
    keepDirtyValues?: boolean;
    isNudgeUpdate?: boolean;
};
type OnRefresh = (options: EventDataRefreshOptions) => void;

function useRefreshEventData(deps: {
    eventMetadataStore: EventPanelMetadataStore;
    locallyRemovedEventFormFieldsStore: LocallyRemovedEventPanelFormFieldsStore;
    eventForm: UseFormReturn<EventPanelFormType, any, undefined>;
    onRefresh?: OnRefresh;
    eventDataHashesRef: MutableRefObject<{
        form: string;
        metadata: string;
    }>;
}) {
    const setFormHasHadDataSetAtLeastOnce = useSetFormHasHadDataSetAtLeastOnceContext();
    const {
        onRefresh,
        eventMetadataStore,
        locallyRemovedEventFormFieldsStore,
        eventForm,
        eventDataHashesRef,
    } = deps;
    return useCallback(
        (options: EventDataRefreshOptions) => {
            const { eventPanelView, phoneCall, keepDirtyValues, isNudgeUpdate, location } = options;

            onRefresh?.(options);

            if (eventDataHashesRef.current.metadata !== eventPanelView.metadataHash) {
                eventMetadataStore.setEventPanelMetadata(eventPanelView.metadata);
                eventDataHashesRef.current.metadata = eventPanelView.metadataHash ?? '';
            }

            // keepDirtyValues is falsey when we're calling refreshEventPanel directly from saveEventMutation.
            // We override our performance motivated hash check in this case to ensure that the form is reset
            // when a user manually saves that the form's dirty fields are reset.
            // Without this check, a race condition can arise where
            // - a user saves an event
            // - the nudge related to the event fully resolves and calls refreshEventPanel _before_ saveEventMutation calls refreshEventPanel in its onSuccess
            // - correspondingly refreshEventPanel is called and our hashes are updated, but the dirty fields wont be reset
            // - later, our save event mutation resolves with onSuccess, but the hashes match so the form is not reset
            // - the fields remain dirty and so the user will see the "dirty active event" footer
            if (eventDataHashesRef.current.form !== eventPanelView.formHash || !keepDirtyValues) {
                // timeout to separate out event panel metadata renders from form reset. This seems to improve rendering performance
                setTimeout(() => {
                    const localFormData = { ...eventForm.getValues() };
                    const remoteFormData = { ...eventPanelView.form };
                    let preservedFormFields: Partial<EventPanelFormType> | undefined;

                    // If we get an update via a nudge, we need to preserve the non-persisted array-based values in the form.
                    if (isNudgeUpdate) {
                        preservedFormFields = preserveLocallyChangedFields(
                            locallyRemovedEventFormFieldsStore.locallyRemovedFields,
                            localFormData,
                            remoteFormData as EventPanelFormType,
                            eventForm.formState
                        );
                    }

                    // Reset the form
                    eventForm.reset(
                        {
                            ...(remoteFormData as EventPanelFormType),
                            ...preservedFormFields,
                            phoneCall: phoneCall
                                ? phoneCallDtoToPhoneCallOption(phoneCall)
                                : eventPanelView.form.phoneCall,
                            location: eventPanelView.form.location || location,
                            oriAliasOption: eventForm.getValues('oriAliasOption'),
                        },
                        {
                            keepDirtyValues: !!keepDirtyValues,
                        }
                    );

                    setFormHasHadDataSetAtLeastOnce(true);

                    // Keep our local changes until the user actually hits save
                    if (!isNudgeUpdate) {
                        locallyRemovedEventFormFieldsStore.onUserSaveForm();
                    }
                }, 10);

                eventDataHashesRef.current.form = eventPanelView.formHash ?? '';
            }
        },
        [
            onRefresh,
            eventMetadataStore,
            locallyRemovedEventFormFieldsStore,
            eventForm,
            setFormHasHadDataSetAtLeastOnce,
            eventDataHashesRef,
        ]
    );
}

export function useIntegratedEventState(props: {
    eventId?: number;
    onRefresh?: OnRefresh;
    queryKey?: string;
    initialPhoneCall?: PhoneCallDto;
    initialLocation?: LocationStoreView;
    selfInitiate?: EventPanelSelfInitiate;
    enabled?: boolean;
    queryOptions?: {
        cacheTime: UseQueryOptions['cacheTime'];
    };
}) {
    const eventDataHashesRef = useRef<{ form: string; metadata: string }>({
        form: '',
        metadata: '',
    });
    const eventMetadataStore = useState(() => new EventPanelMetadataStore())[0];
    const locallyRemovedEventFormFieldsStore = useLocallyRemovedEventFormFieldsStore();
    const formHasHadDataSetAtLeastOnce = useFormHasHadDataSetAtLeastOnceContext();
    const eventPanelFormValidationResolver = useEventPanelFormValidationResolver(
        eventMetadataStore
    );

    const eventForm = useForm<EventPanelFormType>({
        defaultValues: eventPanelFormInitialValues,
        resolver: eventPanelFormValidationResolver,
        mode: 'onBlur',
    });

    const eventQuery = useEventPanelViewQuery({
        eventId: props.eventId,
        queryKey: props.queryKey ?? props.eventId,
        phoneCallDto: props.initialPhoneCall,
        selfInitiate: props.selfInitiate,
        queryOptions: props.queryOptions,
    });

    useIntegratedEventPanelViewQuery({
        enabled: props.enabled,
        eventId: props.eventId,
        query: eventQuery,
        onUpdate: (eventPanelView) =>
            refreshEventData({
                eventPanelView,
                phoneCall: undefined,
                keepDirtyValues: true,
                isNudgeUpdate: true,
            }),
    });

    const refreshEventData = useRefreshEventData({
        eventDataHashesRef,
        eventForm,
        eventMetadataStore,
        locallyRemovedEventFormFieldsStore,
        onRefresh: props.onRefresh,
    });

    const maybeEventPanelView = eventQuery.data;
    useStableEffect(() => {
        if (maybeEventPanelView) {
            refreshEventData({
                eventPanelView: maybeEventPanelView,
                phoneCall: !formHasHadDataSetAtLeastOnce ? props.initialPhoneCall : undefined,
                location: !formHasHadDataSetAtLeastOnce ? props.initialLocation : undefined,
                keepDirtyValues: true,
            });
        } else if (eventQuery.isLoading) {
            eventQuery.refetch();
        }
    }, maybeEventPanelView);

    return {
        eventForm,
        eventQuery,
        refreshEventData,
        eventMetadataStore,
    };
}
